import { FormikErrors, FormikProps, getIn } from "formik";

import { TypeHelper } from "./type";

export type FormikErrorValueType<TValues> = string | string[] | FormikErrors<TValues>;

export type CustomFormikSubProps<SubValues> = Pick<
  FormikProps<SubValues>,
  "values" | "errors" | "touched" | "setFieldError"
> & {
  /** Manually set sub values object (values -> sub values -> fields).  */
  setValues: (values: any, shouldValidate?: boolean) => void;
  /** Set value of form field directly. */
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
};

export class FormikHelper {
  public static isErrorsString<TValues>(
    errors: Nil<FormikErrorValueType<TValues>>,
  ): errors is string {
    return !!errors && TypeHelper.isString(errors);
  }

  public static isErrorsStringArray<TValues>(
    errors: Nil<FormikErrorValueType<TValues>>,
  ): errors is string[] {
    return !!errors && TypeHelper.isArray(errors) && TypeHelper.isArrayOfString(errors);
  }

  public static isErrorsObject<TValues>(
    errors: Nil<FormikErrorValueType<TValues>>,
  ): errors is FormikErrors<TValues> {
    return (
      !!errors &&
      !TypeHelper.isString(errors) &&
      TypeHelper.isObject(errors) &&
      !TypeHelper.isEmptyObject(errors)
    );
  }

  public static isErrorsArray<TValues>(
    errors: Nil<FormikErrorValueType<TValues>> | Nil<FormikErrorValueType<TValues>[]>,
  ): errors is FormikErrors<TValues>[] {
    if (!errors || !Array.isArray(errors) || errors.length === 0) {
      return false;
    }
    return typeof errors[0] !== "string";
  }

  /** Creates facade on base FormikProps<Values> for nested form object. */
  public static getSubProps<Values, SubValues>(
    formikProps: FormikProps<Values> | CustomFormikSubProps<Values | null | undefined>,
    subPath: string,
    getSubValues: (values: Values) => SubValues, // Exclude<SubValues, null | undefined>,
  ): CustomFormikSubProps<SubValues> {
    return {
      values: getIn(formikProps.values, subPath) || undefined,
      errors: getIn(formikProps.errors, subPath) || undefined,
      touched: getIn(formikProps.touched, subPath) || undefined,
      setValues: (values: any, shouldValidate?: boolean) =>
        formikProps.setFieldValue(subPath, values, shouldValidate),
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) =>
        formikProps.setFieldValue([subPath, field].join("."), value, shouldValidate),
      setFieldError: (field: string, message: string | undefined) =>
        formikProps.setFieldError([subPath, field].join("."), message),
    };
  }
}
